<template>
    <div class="b-filter-editor container-fluid" >
        <p v-if="filterCount">Add up to {{ maxFilterCount }} search criteria.</p>
        <p v-if="!filterCount">Click <q>Add criterion</q> button to add up to {{ maxFilterCount }} criteria.</p>
        <div
            class="row b-filter-criterion align-baseline mb-2"
            v-for="(filter, index) in props.value?.filters.filter((f) => f.property != 'id')"
            :key="index">
            <div class="col b-filter-controls align-baseline">
                <div class="row align-baseline">
                    <div class="col ps-0 pe-1 align-baseline">
                        <select
                            class="form-select"
                            aria-label="filter"
                            placeholder="Field Name "
                            v-model="filter.property"
                            @change="setFilterProperty(filter, $event)"
                            @keydown.enter.prevent="handleKeydown">
                            <option
                                v-for="item in fieldItems"
                                v-bind:value="item.value">
                                {{ item.text }}
                            </option>
                        </select>
                    </div>
                    <div class="col-2 px-0 align-baseline">
                        <select
                            class="form-select"
                            aria-label="filter"
                            placeholder="Operation"
                            v-model="filter.operation"
                            @change="filter.operation = getFilterValue($event)"
                            :disabled="!filter.property"
                            @keydown.enter.prevent="handleKeydown">
                            <option
                                v-for="item in operationItems(filter)"
                                v-bind:value="item.value">
                                {{ item.text }}
                            </option>
                        </select>
                    </div>
                    <div class="col ps-1 pe-0 align-baseline">
                        <component
                            v-bind:is="editorComponent(filter)"
                            :disabled="!filter.property"
                            v-model="filter.value"
                            :items="optionItems(filter)"
                            @change="handleComponentChange(index, $event, filter)"
                            @update:modelValue="handleComponentChange(index, $event, filter)"
                            @keydown.enter.prevent="handleKeydown($event)"></component>
                    </div>
                </div>
            </div>
            <div class="col b-filter-actions col-auto align-baseline d-flex align-items-center px-0">
                <button
                    @click.prevent="deleteFilter(index)"
                    @keydown.enter.prevent="handleKeydown"
                    class="btn btn-link text-decoration-none">
                    Remove
                </button>
            </div>
        </div>
        <div class="b-filter-button-bar mt-2">
            <span
                v-if="!canAddFilter"
                class="b-filter-max-count"
                >You can't add more than {{ maxFilterCount }} criteria.</span
            >
            <button ref="addButton"
                @click.prevent="addFilter($event)"
                @keydown.enter.prevent="handleKeydown"
                :disabled="!canAddFilter"
                class="btn btn-success">
                Add Criterion
            </button>
        </div>
    </div>
</template>

<script lang="ts">
import DateField from "../Misc/DateField.vue";
import TextField from "../Misc/TextField.vue";
import ListField from "../Misc/ListField.vue";

export default {
    name: "BFilterEditor",
    components: {
        // make components available under their "editor" aliases
        TextEditor: TextField,
        ListEditor: ListField,
        DateEditor: DateField,
    },
};
</script>
<script lang="ts" setup>
import {defineComponent, watch, ref, computed} from "vue";
import {BFilterOptions, BFilterableField, BFilter} from "./BFilterEditor.types";
import {VItem} from "../../types";

const props = withDefaults(
    defineProps<{
        value?: BFilterOptions;
        fields?: BFilterableField[];
        maxFilterCount?: number;
    }>(),
    {
        fields: () => [],
        maxFilterCount: 3,
    }
);
const emit = defineEmits(["addFilter", "removeFilter", "updateFilter", "submit"]);
var addButton = ref<any | null>();
const filterCount = computed(() => {
    if (props.value == undefined || props.value.filters == undefined) {
        return 0;
    }

    return props.value.filters.filter((f: any) => f.property != "id").length;
});

const fieldItems = computed((): any[] => {
    return props.fields!.map((it) => ({
        text: it.label,
        value: it.property,
    }));
});

const canAddFilter = computed((): boolean => {
    return props.value!.filters.length < props.maxFilterCount;
});

function getFilterValue(event: any): any {
    if (event != null && event.currentTarget != null && event.currentTarget.value != null) {
        return event.currentTarget.value;
    }

    return "";
}

function operationItems(filter: BFilter): VItem[] {
    var field = props.fields.filter((it: {property: string | null}) => it.property == filter.property)[0];
    if (field == null) {
        return [];
    }
    return field.operations.map((it) => ({
        text: it.label,
        value: it.key,
        default: it.default,
    }));
}

function optionItems(filter: BFilter) {
    var field = props.fields.filter((it: {property: string | null}) => it.property == filter.property)[0];

    if (field == null) {
        return [];
    }

    return field.editorOptions;
}

function editorComponent(filter: BFilter): string {
    var field = props.fields.filter((it: {property: string | null}) => it.property == filter.property)[0];
    if (field == null) {
        return "text-editor";
    }
    return field.editorComponent;
}

function addFilter(event: any) {
    emit("addFilter");

    if(addButton.value){
        addButton.value.focus();
    }
}

function deleteFilter(index: number) {
    emit("removeFilter", index);
    
    if(addButton.value){
        addButton.value.focus();
    }
}

function setFilterProperty(filter: BFilter, event: any) {
    let value = event.currentTarget.value;
    filter.property = value;
    var items = operationItems(filter);
    var defaultItem = items.find((it) => it.default);
    filter.operation = defaultItem ? defaultItem.value : items[0].value;
    filter.value = null;
}

function handleComponentChange(index: number, event: any, filter: BFilter) {
    if (event != null) {
        if (typeof event == "string") {
            filter.value = event;
        } else if (event.target != null) {
            filter.value = event.target.value;
        } else {
            filter.value = event.toString();
        }
    } else {
        filter.value = "";
    }
    let data = {index: index, filter: filter};
    emit("updateFilter", data);
}

function handleKeydown(event: KeyboardEvent) {
    emit("submit")
}

</script>
<style lang="scss">
.b-filter-button-bar {
    text-align: right;
}

.b-filter-max-count {
    margin: 0 8px;
    color: #757575;
}
</style>
